'use client';

import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import ModalsContainer from '@wconcept/wconcept-ui-renewal/src/components/molecules/Modal/ModalsContainer';
import {
  ModalContentType,
  ModalProps,
} from '@wconcept/wconcept-common/models/display/common';
import { usePathname } from 'next/navigation';

interface IProps {
  children?: ReactNode;
}

interface ModalsContextProps {
  openModal: <P = any, R = any, C = any>(
    Component: React.JSXElementConstructor<any>,
    props: ModalProps<P, R, C>
  ) => void;
  closeModal: (Component: React.JSXElementConstructor<any>) => void;
  isActiveModal: (
    Component: React.JSXElementConstructor<any>,
    active: boolean
  ) => void;
  modalContent: ModalContentType[];
}

const ModalsContext = createContext<ModalsContextProps>({
  openModal: () => {},
  closeModal: () => {},
  isActiveModal: () => {},
  modalContent: [],
});

const { Provider } = ModalsContext;

function ModalsProvider({ children }: IProps) {
  const [modalContent, setModalContent] = useState<ModalContentType[]>([]);
  const pathname = usePathname();
  const previousPathname = useRef(pathname);
  const closeModal = useCallback(
    (Component: React.JSXElementConstructor<any>) => {
      if (window.history?.state?.page === 'modal') {
        window.onpopstate = null;
        window.history.back();
      }
      setModalContent(modals => {
        const nextModals = modals.filter(
          modal => modal.Component !== Component
        );
        return nextModals.length === modals.length ? modals : nextModals;
      });
    },
    []
  );

  const openModal = useCallback(
    <P, R, C>(
      Component: React.JSXElementConstructor<ModalProps<P, R, C>>,
      params: ModalProps<P, R, C>
    ) => {
      if (params.mode === 'fullPage') {
        window.history.pushState({ page: 'modal' }, '', null);
        window.onpopstate = () => closeModal(Component);
      }
      setModalContent(modals => [
        ...modals,
        {
          Component,
          params: { ...params, path: window.location.pathname },
          isActive: true,
        },
      ]);
    },
    [closeModal]
  );

  const isActiveModal = useCallback(
    (Component: React.JSXElementConstructor<any>, active: boolean) => {
      setModalContent(modals =>
        modals.map(modal =>
          modal.Component === Component ? { ...modal, isActive: active } : modal
        )
      );
    },
    []
  );

  useEffect(() => {
    if (previousPathname.current === pathname) return;

    previousPathname.current = pathname;
    modalContent.forEach(content => {
      closeModal(content.Component);
    });
  }, [closeModal, modalContent, pathname]);

  return (
    <Provider
      value={{
        openModal,
        closeModal,
        isActiveModal,
        modalContent,
      }}
    >
      <ModalsContainer
        modalContent={modalContent}
        openModal={openModal}
        closeModal={closeModal}
        isActiveModal={isActiveModal}
      />
      {children}
    </Provider>
  );
}

export { ModalsProvider, ModalsContext };
